import { serverOptionSelector } from "@store/login/login-selector";
import { useAppSelector } from "@store/store-helper";
import { useCallback, useEffect, useState } from "react";
import {
  getRedirectUrlParam,
  getVisitedServerIdentifier,
} from "@utils/ulm-utils";
import { CLIENT_CONFIGURATION } from "@constants/ulm-constants";
import { ulmConfigClientSelector } from "@store/ulm-config/ulm-config-selector";
import { Login } from "@pages/login/login";
import { LoadingScreen } from "@flat-ui";
import { useSearchParams } from "react-router-dom";
import { redirectClient } from "@utils/client-utils";
import { runtimeConfig } from "@src/runtime-config";
import { faroCoreApiClient } from "@api/core-api/core-api-utils";
import { getUrlBasedOnServer } from "@utils/servers-utils";
import { setAutomationTestUserTag } from "@utils/sentry-utils";

/**
 * Wrapper component for the login page.
 * For some ULM clients it checks first if there is a preselected server and
 * if the user is already logged-in in the same server. If true then skips
 * the login workflow and automatically forwards the user to the redirect URL.
 * Otherwise it shows the login workflow.
 */
export function LoginWrapper(): JSX.Element {
  const client = useAppSelector(ulmConfigClientSelector);
  const preselectedServer = useAppSelector(serverOptionSelector);
  const [searchParams] = useSearchParams();
  const currentServer = getVisitedServerIdentifier();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Handles redirect
  const redirect = useCallback(() => {
    if (!currentServer) {
      return;
    }

    // Default XG redirect URL after a successful login
    let { dashboard2Url } = getUrlBasedOnServer(currentServer);

    // Redirect URL passed via query params
    const redirectUrlParam = getRedirectUrlParam(
      searchParams,
      currentServer,
      client
    );

    // If there is redirect URL passed via query params validate it.
    if (redirectUrlParam) {
      try {
        new URL(redirectUrlParam);
        dashboard2Url = redirectUrlParam;
      } catch (_) {
        // If redirect URL from query params is invalid then keep using the default redirect URL
      }
    }

    // Redirect through the CoreAPI "/redirectTo" route since this route takes care of
    // appending special query params for each redirect URL domain.
    const coreApiUrl = new URL("redirectTo", runtimeConfig.urls.apiBaseUrl);
    coreApiUrl.searchParams.set("redirectUrl", dashboard2Url);
    redirectClient({
      client,
      redirectUrl: coreApiUrl.href,
    });
  }, [client, currentServer, searchParams]);

  // Check if the user is already logged in.
  // If true then skip the login workflow and redirect. Otherwise show the login workflow.
  const checkLogin = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    try {
      // Fetch the authenticated user so the Sentry automation/test tag is set even in this
      // redirect flow, where the login form (and its email input) is never rendered.
      const user = await faroCoreApiClient.users.getLoggedInUser();
      setAutomationTestUserTag(user.email);
      redirect();
    } catch (_) {
      // There is no need to handle the error in case it fails to check the login status.
      // Simply hide the loading animation.
      setIsLoading(false);
    }
  }, [redirect]);

  // Checks if:
  // - login should be checked for the current client AND
  // - current and preselected server are the same.
  // - If true then check the login status of the user. Otherwise show the login workflow.
  useEffect(() => {
    if (
      CLIENT_CONFIGURATION[client].shouldCheckLogin &&
      currentServer !== null &&
      preselectedServer !== undefined &&
      currentServer === preselectedServer
    ) {
      void checkLogin();
    }
  }, [checkLogin, client, currentServer, preselectedServer]);

  return isLoading ? <LoadingScreen /> : <Login />;
}
